import { Navigate, Outlet, useLocation } from "react-router-dom";
import type { FC } from "react";
import { SectionWrapper } from "../components/common/SectionWrapper";
import { CaseProvider } from "../contexts/CaseContext";
import { IndividualProvider } from "../contexts/IndividualContext";
import { CaseHeader } from "../features/caseIde";
import {
  MatterProvider,
  useMatter
} from "../features/caseIde/context/MatterContext";
import { rehomeCasePath } from "../features/caseIde/routes";
import { useCaseRoute } from "../features/caseIde/useCaseRoute";

/**
 * Keep the URL on the client-scoped form. `/cases/:id/...` is only for cases
 * with no client link, so once the matter says which client this case belongs
 * to (or that the client in the URL is wrong) we move to the right root and
 * keep the page and query underneath.
 */
const CanonicalCaseUrl: FC = () => {
  const route = useCaseRoute();
  const { pathname, search } = useLocation();
  const { matter, foreignNationalId } = useMatter();
  if (!matter || !foreignNationalId) return null;
  if ((route.foreignNationalId ?? null) === foreignNationalId) return null;
  const target = rehomeCasePath(pathname, search, {
    individualId: route.individualId,
    foreignNationalId
  });
  return target ? <Navigate to={target} replace /> : null;
};

/**
 * Shell for every case route (`/clients/:fnId/cases/:id/*`, `/cases/:id/*`):
 * registers the case with the app tab bar, then the workspace.
 */
const IndividualCase = () => {
  const { individualId } = useCaseRoute();
  if (!individualId) return null;

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden">
      <IndividualProvider individualId={individualId}>
        <CaseProvider individualId={individualId}>
          <MatterProvider individualId={individualId}>
            <CanonicalCaseUrl />
            <CaseHeader />
            <SectionWrapper
              withBackNav={false}
              padding={0}
              my={0}
              flex="1"
              minH={0}
            >
              <Outlet />
            </SectionWrapper>
          </MatterProvider>
        </CaseProvider>
      </IndividualProvider>
    </div>
  );
};

export default IndividualCase;
